import { apiEndpoints } from '@/lib/env'
import type { LoginFormData, SignUpFormData, LoginRequest, SignUpRequest, AuthResponse, PhoneVerificationRequest, ResendCodeRequest, ValidatePhoneRequest, RegistrationDetailsData, PhoneChallengeRequest, PhoneChallengeResponse, PhoneChallengeVerifyRequest, User } from '@/types/auth'
import { meiucaService } from './MeiucaService'
import Cookies from 'js-cookie'
import { userManager } from '@/managers/userManager'

class AuthService {
  private service = meiucaService

  async login(data: LoginFormData): Promise<AuthResponse> {
    const loginRequest: LoginRequest = {
      provider: "password",
      identifier: `+55${data.phone}`,
      secret: data.password,
      rememberMe: data.rememberMe
    }

    const response = await this.service.post('/auth/sign-in', loginRequest)
    const authData: AuthResponse = response.data

    // Store tokens
    Cookies.set('accessToken', authData.data.accessToken, {
      expires: data.rememberMe ? 30 : 1 // 30 days or 1 day
    })
    Cookies.set('refreshToken', authData.data.refreshToken, {
      expires: data.rememberMe ? 30 : 7 // 30 days or 7 days
    })

    return authData
  }

  async signUpWithDetails(phone: string, verificationToken: string, data: RegistrationDetailsData): Promise<AuthResponse> {
    const signUpRequest = {
      name: data.name,
      // O telefone do perfil vem do verificationToken (verificado via OTP)
      // no backend — não mandamos `phoneNumber`. O `phone` aqui só compõe o
      // identifier de login (credential).
      verificationToken,
      // documentType + documentNumber obrigatórios no backend a partir
      // de MEIUC-119. Hoje aceitamos só CPF nesse fluxo de signup.
      documentType: 'cpf' as const,
      documentNumber: data.cpf,
      credential: {
        provider: "password",
        identifier: `+55${phone}`,
        secret: data.password
      }
    }

    const response = await this.service.post('/auth/sign-up', signUpRequest)
    const authData: AuthResponse = response.data

    // Store tokens (similar to login)
    Cookies.set('accessToken', authData.data.accessToken, {
      expires: 30 // 30 days
    })
    Cookies.set('refreshToken', authData.data.refreshToken, {
      expires: 30 // 30 days
    })

    return authData
  }

  async logout(): Promise<void> {
    try {
      // Best-effort server-side logout; never block the local sign-out on it.
      await this.service.post('/logout')
    } catch {
      // Ignore — the route may be absent on this surface; tokens are cleared below.
    } finally {
      this.service.clearTokens()
      this.clearUserCache()
    }
  }

  async getProfile(): Promise<User> {
    const response = await this.service.get('/users/me')
    return response.data.data
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken()
  }

  getAccessToken(): string | undefined {
    return this.service.getAccessToken()
  }

  // User cache management - delegates to userManager
  saveUserToCache(user: User): void {
    userManager.saveProfile(user)
  }

  getUserFromCache(): User | null {
    return userManager.getProfile()
  }

  clearUserCache(): void {
    userManager.clearProfile()
  }

  async sendPhoneChallenge(phoneNumber: string): Promise<PhoneChallengeResponse> {
    const request: PhoneChallengeRequest = {
      phoneNumber: `+55${phoneNumber}`
    }

    const response = await this.service.post(apiEndpoints.auth.challengesPhone, request)
    return response.data as PhoneChallengeResponse
  }

  async verifyPhoneChallenge(phoneNumber: string, code: string): Promise<string> {
    const request: PhoneChallengeVerifyRequest = {
      phoneNumber: `+55${phoneNumber}`,
      code: code
    }

    const response = await this.service.post(apiEndpoints.auth.challengesPhoneVerify, request)
    // Backend retorna { data: { verificationToken } }. Esse token prova que o
    // telefone foi verificado e é exigido no sign-up.
    return response.data.data.verificationToken as string
  }

  async resendPhoneChallenge(phoneNumber: string): Promise<PhoneChallengeResponse> {
    const request: PhoneChallengeRequest = {
      phoneNumber: `+55${phoneNumber}`
    }

    const response = await this.service.post(apiEndpoints.auth.challengesPhoneResend, request)
    return response.data as PhoneChallengeResponse
  }
}

// Export singleton instance
export const authService = new AuthService()
